import { useQuery } from '@tanstack/react-query';
import { reportApiError } from '@/utils/errorReporter';
import { USE_MOCK, analyticsApi, ApiError, retryOnTransientError, type AnalyticsFilterParams } from '@/api/client';
import {
  MOCK_RESULTS,
  MOCK_PEOPLE,
} from '@/data/mockAnalytics';
import type {
  AnalyticsSummaryResponse,
  DimensionScore,
  FundamentalsResponse,
  HeatmapResponse,
  OnaResponse,
  GenerationalResponse,
  GenerationalParticipantsResponse,
  BiasResponse,
  OmissionsResponse,
  PersonalResponse,
} from '@/api/types';

/** Calls fn and returns fallback on 404 or network errors (service down). Re-throws other errors. */
async function safeCall<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err: unknown) {
    if (err instanceof ApiError && err.status === 404) return fallback;
    if (err instanceof TypeError) return fallback; // ERR_CONNECTION_REFUSED / Failed to fetch
    throw err;
  }
}

// ─── Derived summary from mock results ────────────────────────────────────────

function buildMockSummary(): AnalyticsSummaryResponse {
  const numeric = MOCK_RESULTS.filter(result => typeof result.score === 'number');
  const overallAverage = numeric.length > 0
    ? +(numeric.reduce((sum, result) => sum + (result.score as number), 0) / numeric.length).toFixed(2)
    : 0;
  const noObs = MOCK_RESULTS.filter(result => result.score === 'no_obs').length;
  const responseRate = MOCK_RESULTS.length > 0
    ? Math.round(((MOCK_RESULTS.length - noObs) / MOCK_RESULTS.length) * 100)
    : 0;

  const scoresByFundamental = new Map<string, number[]>();
  for (const result of numeric) {
    const scores = scoresByFundamental.get(result.fundamentalId) ?? [];
    scores.push(result.score as number);
    scoresByFundamental.set(result.fundamentalId, scores);
  }

  const dimensions: DimensionScore[] = [...scoresByFundamental.entries()]
    .map(([fundamentalId, scores]) => ({
      id: fundamentalId,
      name: fundamentalId,
      isBehavior: false,
      fundamentalId,
      fundamentalName: fundamentalId,
      score: +(scores.reduce((sum, score) => sum + score, 0) / scores.length).toFixed(2),
      evaluations: scores.length,
      noObs: 0,
      distribution: [0, 0, 0, 0],
    }))
    .sort((first, second) => second.score - first.score);

  return {
    totalParticipants: new Set(MOCK_RESULTS.map(result => result.evaluatedId)).size,
    totalEvaluations: MOCK_RESULTS.length,
    overallAverage,
    responseRate,
    avgPerDay: +(MOCK_RESULTS.length / 30).toFixed(1),
    uniqueEvaluators: new Set(MOCK_RESULTS.map(result => result.evaluatorId)).size,
    dimensions,
    topBehaviors: dimensions.slice(0, 3),
    opportunities: [...dimensions].reverse().slice(0, 3),
    scoreByOu: [],
    omissionsByUnit: [],
  };
}

const EMPTY_SUMMARY: AnalyticsSummaryResponse = {
  totalParticipants: 0, totalEvaluations: 0, overallAverage: 0, responseRate: 0,
  avgPerDay: 0, uniqueEvaluators: 0, dimensions: [], topBehaviors: [],
  opportunities: [], scoreByOu: [], omissionsByUnit: [],
};

const EMPTY_HEATMAP: HeatmapResponse = { columns: [], rows: [] };

/**
 * Opciones comunes de las consultas de analítica. `isEnabled: false` mantiene la consulta en
 * espera: lo usa AnalyticsView mientras el alcance de OUs del usuario todavía no está resuelto,
 * para no pedir datos de todo el tenant a un administrador que solo puede ver su rama.
 */
export interface AnalyticsQueryOptions {
  isEnabled?: boolean;
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useAnalyticsSummary(filters: AnalyticsFilterParams = {}, options: AnalyticsQueryOptions = {}) {
  return useQuery<AnalyticsSummaryResponse>({
    queryKey: ['analytics', 'summary', filters],
    enabled: options.isEnabled ?? true,
    retry: false,
    queryFn: () => USE_MOCK
      ? Promise.resolve(buildMockSummary())
      : safeCall(() => analyticsApi.getSummary(filters), EMPTY_SUMMARY),
  });
}

export function useAnalyticsFundamentals(filters: AnalyticsFilterParams = {}, options: AnalyticsQueryOptions = {}) {
  return useQuery<FundamentalsResponse>({
    queryKey: ['analytics', 'fundamentals', filters],
    enabled: options.isEnabled ?? true,
    retry: false,
    queryFn: (): Promise<FundamentalsResponse> => USE_MOCK
      ? Promise.resolve([] as FundamentalsResponse)
      : safeCall(() => analyticsApi.getFundamentals(filters), [] as FundamentalsResponse),
  });
}

export function useAnalyticsHeatmap(filters: AnalyticsFilterParams = {}, options: AnalyticsQueryOptions = {}) {
  return useQuery<HeatmapResponse>({
    queryKey: ['analytics', 'heatmap', filters],
    enabled: options.isEnabled ?? true,
    retry: false,
    queryFn: () => USE_MOCK
      ? Promise.resolve(EMPTY_HEATMAP)
      : safeCall(() => analyticsApi.getHeatmap(filters), EMPTY_HEATMAP),
  });
}

const EMPTY_ONA: OnaResponse = { edges: [] };

export function useAnalyticsOna(filters: AnalyticsFilterParams = {}, options: AnalyticsQueryOptions = {}) {
  return useQuery<OnaResponse>({
    queryKey: ['analytics', 'ona', filters],
    enabled: options.isEnabled ?? true,
    retry: false,
    queryFn: async () => {
      if (USE_MOCK) {
        return {
          edges: MOCK_PEOPLE.slice(1).map((person, index) => ({
            evaluatorId: MOCK_PEOPLE[index]!.id,
            evaluatedId: person.id,
            interactionCount: 5,
          })),
        };
      }
      return safeCall(() => analyticsApi.getOna(filters), EMPTY_ONA);
    },
  });
}

export function useAnalyticsGenerational(filters: AnalyticsFilterParams = {}, options: AnalyticsQueryOptions = {}) {
  return useQuery<GenerationalResponse>({
    queryKey: ['analytics', 'generational', filters],
    enabled: options.isEnabled ?? true,
    retry: false,
    queryFn: (): Promise<GenerationalResponse> => USE_MOCK
      ? Promise.resolve([] as GenerationalResponse)
      : safeCall(() => analyticsApi.getGenerational(filters), [] as GenerationalResponse),
  });
}

export function useAnalyticsGenerationalParticipants(filters: AnalyticsFilterParams = {}, options: AnalyticsQueryOptions = {}) {
  return useQuery<GenerationalParticipantsResponse>({
    queryKey: ['analytics', 'generational-participants', filters],
    enabled: options.isEnabled ?? true,
    retry: false,
    queryFn: (): Promise<GenerationalParticipantsResponse> => USE_MOCK
      ? Promise.resolve([] as GenerationalParticipantsResponse)
      : safeCall(() => analyticsApi.getGenerationalParticipants(filters), [] as GenerationalParticipantsResponse),
  });
}

export function useAnalyticsBias(filters: AnalyticsFilterParams = {}, options: AnalyticsQueryOptions = {}) {
  return useQuery<BiasResponse>({
    queryKey: ['analytics', 'bias', filters],
    enabled: options.isEnabled ?? true,
    retry: false,
    queryFn: () => USE_MOCK
      ? Promise.resolve([] as BiasResponse)
      : safeCall(() => analyticsApi.getBias(filters), [] as BiasResponse),
  });
}

const EMPTY_OMISSIONS: OmissionsResponse = {
  totalParticipants: 0,
  participantsWithZeroResponses: 0,
  participants: [],
};

export function useAnalyticsOmissions(filters: AnalyticsFilterParams = {}, options: AnalyticsQueryOptions = {}) {
  return useQuery<OmissionsResponse>({
    queryKey: ['analytics', 'omissions', filters],
    enabled: options.isEnabled ?? true,
    retry: false,
    queryFn: () => USE_MOCK
      ? Promise.resolve(EMPTY_OMISSIONS)
      : safeCall(() => analyticsApi.getOmissions(filters), EMPTY_OMISSIONS),
  });
}

/**
 * Dashboard personal del usuario.
 *
 * A diferencia del resto de consultas de analítica, aquí NO se usa `safeCall`: rellenar
 * con ceros ante un fallo hacía que un servicio caído se viera exactamente igual que
 * "todavía no tienes retroalimentación". El error se propaga para que la vista pueda
 * mostrar un mensaje distinto y registrarlo. `undefined` significa que el servidor
 * respondió 204: la persona aún no tiene agregado de puntajes.
 */
export function useAnalyticsPersonal(userId?: string) {
  return useQuery<PersonalResponse | null, Error, PersonalResponse | undefined>({
    queryKey: ['analytics', 'personal', userId],
    retry: retryOnTransientError,
    // El 204 viaja como `null` (ver usePersonalGrowth) y vuelve a `undefined` en `select`.
    select: personal => personal ?? undefined,
    queryFn: async (): Promise<PersonalResponse | null> => {
      if (USE_MOCK) {
        return {
          evaluatedId: userId ?? 'demo-user',
          avgScore: 3.4,
          trend: 0.2,
          fundamentalScores: [],
          responseCount: 0,
          lastUpdatedAt: new Date().toISOString(),
        };
      }
      try {
        return (await analyticsApi.getPersonal(userId)) ?? null;
      } catch (apiError) {
        reportApiError('analytics.personal', apiError);
        throw apiError;
      }
    },
  });
}
